import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { SINGLE_CATEGORIES } from '../../utils/studentMemos';
import { classEvidenceCounts, defaultIncludeScoring, scoringEvidence } from '../../utils/studentRecordsHandoff';

const sum = (list, key) => list.reduce((total, item) => total + item[key], 0);

/**
 * 반 화면 '반 전체 생기부 쓰기': 영역을 고르면 학생마다 그 영역 메모(와 고르면 채점 결과)로 다중 생성에 넘긴다.
 * 메모가 하나도 없고 채점만 있는 반은 처음에 교과세특을 골라 둔다(예전처럼 바로 채점 결과로 쓸 수 있게).
 */
const ClassWriteSection = ({ students, notesBySid, notesStatus, recordsStatus, starting, onStart, onRetry }) => {
  const [picked, setPicked] = useState(null);
  const [withScoring, setWithScoring] = useState(null);
  const notesReady = notesStatus === 'ready';
  const recordsDone = recordsStatus === 'ready' || recordsStatus === 'error';
  const scoredStudents = recordsStatus === 'ready' ? students.filter(item => scoringEvidence(item).length > 0).length : 0;
  const anyMemo = notesReady && Object.values(notesBySid || {}).some(item => (item?.memos || []).length > 0);
  const category = picked || (!anyMemo && scoredStudents > 0 ? '교과' : '행동발달');
  const includeScoring = scoredStudents > 0 && (withScoring === null ? defaultIncludeScoring(category) : withScoring);
  const label = SINGLE_CATEGORIES.find(item => item.category === category)?.label || '생기부';

  const counts = useMemo(
    () => (notesReady ? classEvidenceCounts({ students, notesBySid, category, includeScoring }) : []),
    [notesReady, students, notesBySid, category, includeScoring],
  );
  const ready = counts.filter(item => item.memos + item.scoring > 0);
  const missing = counts.length - ready.length;
  const loading = !notesReady && notesStatus !== 'error';
  const scoringTotal = recordsStatus === 'ready' ? students.reduce((total, item) => total + scoringEvidence(item).length, 0) : 0;

  if (notesReady && recordsDone && !anyMemo && scoredStudents === 0) {
    return (
      <section className="class-students-cta" data-testid="class-write-empty">
        <div>
          <strong>메모하거나 채점하면 반 전체 생기부를 쓸 수 있어요</strong>
          <span>위 학생 칸을 눌러 한 줄씩 남기거나, 이 반 답안을 채점해 보세요.</span>
        </div>
        <Link className="classes-btn-primary class-students-link-btn" to="/scoring">이 반 답안 채점하기</Link>
      </section>
    );
  }

  let countText = '';
  if (loading || !recordsDone) countText = '기록 불러오는 중…';
  else if (ready.length === 0) countText = `이 영역에 넣을 메모가 없어요${scoredStudents > 0 && !includeScoring ? '. 채점 결과를 함께 넣어 보세요' : ''}`;
  else {
    countText = `${ready.length}명 · 메모 ${sum(ready, 'memos')}${includeScoring ? ` · 채점 ${sum(ready, 'scoring')}` : ''}`;
    if (missing > 0) countText += ` · 근거 없는 ${missing}명은 빼요`;
  }

  return (
    <section className="class-students-write class-write" data-testid="class-write">
      <strong className="class-students-write-title">반 전체 생기부 쓰기</strong>
      {notesReady && recordsDone && (
        <div className="class-students-chips" role="group" aria-label="영역">
          {SINGLE_CATEGORIES.map(item => (
            <button
              key={item.category}
              type="button"
              aria-pressed={category === item.category}
              className={category === item.category ? 'is-active' : ''}
              onClick={() => setPicked(item.category)}
            >
              {item.label}
            </button>
          ))}
        </div>
      )}
      {notesStatus === 'error' ? (
        <p className="class-students-notes-error" role="alert">
          메모를 불러오지 못했어요.
          <button type="button" className="class-students-inline-btn" onClick={onRetry}>다시 불러오기</button>
        </p>
      ) : (
        <p className="class-students-write-count" data-testid="class-write-count">{countText}</p>
      )}
      {notesReady && scoredStudents > 0 && (
        <label className="class-students-write-toggle">
          <input type="checkbox" checked={includeScoring} onChange={event => setWithScoring(event.target.checked)} />
          채점 결과 {scoringTotal}개 함께 넣기
        </label>
      )}
      <div className="class-students-write-actions">
        <button
          type="button"
          className="classes-btn-primary"
          disabled={starting || loading || !recordsDone || ready.length === 0}
          onClick={() => onStart({ category, includeScoring })}
        >
          {starting ? '불러오는 중…' : notesReady && recordsDone ? `${ready.length > 0 ? `${ready.length}명 ` : ''}${label} 쓰기` : '생기부 쓰기'}
        </button>
      </div>
    </section>
  );
};

export default ClassWriteSection;
